import {
  Alert,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AddressSearchInput from '../../components/AddressSearchInput';
import NumberPicker from '../../components/NumberPicker';
import SearchLoadingIndicator from '../../components/SearchLoadingIndicator';
import UserOption from '../../components/UserOption';
import Course from '../../course/types/Course';
import useInstructorOptions from '../../user/hooks/useInstructorOptions';
import { Permission } from '../../user/types/Permission';
import { useUser } from '../../user/userContext';
import { userHasPermission } from '../../user/userUtils';
import OpenClassAPI from '../OpenClassAPI';
import OpenClassInstructorSearch from './OpenClassInstructorSearch';
import { SessionDraft } from './sessionDraft';
import {
  getDisabledSessionEndHours,
  getDisabledSessionEndMinutes,
  isSessionEndDateDisabled,
  withSessionEndAt,
  withSessionStartAt,
} from './sessionTiming';

const twoColumnGrid = 'grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-2';
const threeColumnGrid = `${twoColumnGrid} lg:grid-cols-3`;
const fourColumnGrid = `${twoColumnGrid} xl:grid-cols-4`;

type SessionFieldsProps = {
  value: SessionDraft;
  courseId?: Course['id'];
  showInstructorSearch?: boolean;
  onChange: (next: SessionDraft) => void;
};

const SessionFields: React.FC<SessionFieldsProps> = ({
  value,
  courseId,
  showInstructorSearch = false,
  onChange,
}) => {
  const { t } = useTranslation();
  const [currentUser] = useUser();
  const [minLength, setMinLength] = useState(3);
  const [inHouseOnly, setInHouseOnly] = useState(false);
  const [maxDistance, setMaxDistance] = useState(true);
  const [warnings, setWarnings] = useState<string[]>([]);
  const canReadInstructors = userHasPermission(
    currentUser,
    Permission.USER_READ_LIST,
  );

  const {
    hasMoreInstructors,
    instructors,
    loadFirstInstructorPage,
    loadMoreInstructors,
    loadingInstructorOptions,
    loadingInstructors,
    searchInstructors,
  } = useInstructorOptions({
    enabled: canReadInstructors,
    hydrateSelectedIds: value.instructorIds,
  });
  const [endAtRejected, setEndAtRejected] = useState(false);

  const patch = (partial: Partial<SessionDraft>) =>
    onChange({ ...value, ...partial });

  const { instructorIds, startAt, endAt } = value;
  useEffect(() => {
    if (
      instructorIds.length === 0 ||
      !startAt ||
      !endAt ||
      !endAt.isAfter(startAt)
    ) {
      setWarnings([]);
      return undefined;
    }
    let cancelled = false;
    const handle = setTimeout(() => {
      OpenClassAPI.validateSession({
        instructorIds,
        startAt: startAt.toISOString(),
        endAt: endAt.toISOString(),
      })
        .then(({ data }) => {
          if (!cancelled) setWarnings(data);
        })
        .catch(() => {
          if (!cancelled) setWarnings([]);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [instructorIds, startAt, endAt]);

  const selectableInstructors = useMemo(
    () =>
      instructors
        .filter(({ id }) => id != null)
        .filter(
          ({ id, courses }) =>
            courseId == null ||
            value.instructorIds.includes(id!) ||
            courses?.some((course) => course.id === courseId),
        ),
    [courseId, instructors, value.instructorIds],
  );

  useEffect(() => {
    if (
      courseId != null &&
      selectableInstructors.length === 0 &&
      hasMoreInstructors &&
      !loadingInstructorOptions
    ) {
      loadMoreInstructors();
    }
  }, [
    courseId,
    hasMoreInstructors,
    loadMoreInstructors,
    loadingInstructorOptions,
    selectableInstructors.length,
  ]);

  return (
    <Form component="div" layout="vertical">
      <div className={twoColumnGrid}>
        <Form.Item className="mb-0" required label={t('common.location')}>
          <AddressSearchInput
            initialValue={value.location || undefined}
            placeholder={t('common.address')}
            size="large"
            onChange={(suggestion) =>
              patch({
                location: suggestion?.address ?? '',
                geolocation: suggestion?.geolocation ?? null,
                placeId: suggestion?.placeId ?? null,
              })
            }
          />
        </Form.Item>
        <Form.Item className="mb-0" label={t('common.note')}>
          <Input.TextArea
            autoSize={{ minRows: 1 }}
            placeholder={t('views.OpenClasses.notePlaceholder')}
            size="large"
            value={value.note}
            onChange={(event) => patch({ note: event.target.value })}
          />
        </Form.Item>
      </div>
      <div
        className={`mt-10 ${
          showInstructorSearch ? fourColumnGrid : threeColumnGrid
        }`}>
        <Form.Item
          className="mb-0"
          required
          label={t('views.OpenClasses.from')}>
          <DatePicker
            className="w-full"
            format="YYYY-MM-DD HH:mm"
            size="large"
            showTime={{ format: 'HH:mm' }}
            value={value.startAt}
            onChange={(startAt) => {
              setEndAtRejected(false);
              onChange(withSessionStartAt(value, startAt));
            }}
          />
        </Form.Item>
        <Form.Item
          className="mb-0"
          required
          label={t('views.OpenClasses.to')}
          validateStatus={endAtRejected ? 'error' : undefined}
          help={
            endAtRejected
              ? t('views.OpenClasses.endAtBeforeStartAt')
              : undefined
          }>
          <DatePicker
            className="w-full"
            disabledDate={(date) =>
              isSessionEndDateDisabled(date, value.startAt)
            }
            disabledTime={(date) => ({
              disabledHours: () =>
                getDisabledSessionEndHours(date, value.startAt),
              disabledMinutes: (hour) =>
                getDisabledSessionEndMinutes(date, value.startAt, hour),
            })}
            format="YYYY-MM-DD HH:mm"
            size="large"
            showTime={{ format: 'HH:mm' }}
            value={value.endAt}
            onChange={(endAt) => {
              const next = withSessionEndAt(value, endAt);
              setEndAtRejected(endAt !== null && next.endAt === null);
              onChange(next);
            }}
          />
        </Form.Item>
        <Form.Item
          className="mb-0"
          required
          label={t('views.OpenClasses.capacity')}>
          <InputNumber
            className="w-full"
            min={1}
            size="large"
            value={value.capacity}
            onChange={(capacity) => patch({ capacity: capacity ?? 0 })}
          />
        </Form.Item>
        {showInstructorSearch && (
          <Form.Item
            className="mb-0"
            label={t('views.OpenClasses.shortestDuration')}>
            <NumberPicker
              max={24}
              min={0}
              size="large"
              value={minLength}
              onChange={setMinLength}
            />
          </Form.Item>
        )}
      </div>
      <div className={`mt-3 ${showInstructorSearch ? fourColumnGrid : ''}`}>
        <Form.Item
          className={`mb-0 ${showInstructorSearch ? 'md:col-span-2' : ''}`}
          label={t('common.instructors')}>
          <Select
            allowClear
            disabled={!canReadInstructors}
            filterOption={false}
            loading={loadingInstructors}
            mode="multiple"
            notFoundContent={
              loadingInstructorOptions ? <SearchLoadingIndicator /> : null
            }
            placeholder={t('views.OpenClasses.selectInstructors')}
            showSearch
            size="large"
            value={value.instructorIds}
            onChange={(instructorIds) => patch({ instructorIds })}
            onDropdownVisibleChange={(open) => {
              if (open) {
                loadFirstInstructorPage('');
              }
            }}
            onPopupScroll={(event) => {
              const target = event.target as HTMLDivElement;
              const isNearBottom =
                target.scrollTop + target.offsetHeight >=
                target.scrollHeight - 32;

              if (isNearBottom && hasMoreInstructors) {
                loadMoreInstructors();
              }
            }}
            onSearch={searchInstructors}>
            {selectableInstructors.map((instructor) => (
              <Select.Option key={instructor.id!} value={instructor.id!}>
                <UserOption currentUser={currentUser} user={instructor} />
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        {showInstructorSearch && (
          <>
            <Form.Item
              className="mb-0"
              label={t('views.OpenClasses.inHouseInstructorsOnly')}>
              <Switch checked={inHouseOnly} onChange={setInHouseOnly} />
            </Form.Item>
            <Form.Item
              className="mb-0"
              label={t('views.OpenClasses.maximumDistance')}>
              <Switch checked={maxDistance} onChange={setMaxDistance} />
            </Form.Item>
          </>
        )}
      </div>
      {warnings.length > 0 && (
        <div className="mt-3 space-y-2">
          {warnings.map((warning) => (
            <Alert key={warning} message={warning} showIcon type="warning" />
          ))}
        </div>
      )}
      {showInstructorSearch && (
        <div className="mt-4">
          <OpenClassInstructorSearch
            courseId={courseId}
            endAt={value.endAt}
            geolocation={value.geolocation}
            inHouseOnly={inHouseOnly}
            maxDistance={maxDistance}
            minLength={minLength}
            placeId={value.placeId}
            selectedInstructorIds={value.instructorIds}
            startAt={value.startAt}
            onAdd={(instructorId) =>
              patch({
                instructorIds: value.instructorIds.includes(instructorId)
                  ? value.instructorIds
                  : [...value.instructorIds, instructorId],
              })
            }
          />
        </div>
      )}
    </Form>
  );
};

export default SessionFields;
